import { DependencyState, initialModelState } from "./initialData";
import { ActionTypes as types } from "./actionTypes";
import { DependencyData } from "./types";
import { ColDef } from "ag-grid-community";
import { AnyAction } from "redux";
import { getFlattenedRow, getRowData } from "./rowDataUtils";
import { formatSuspectedStuckAt } from "../components/dependencies/valueFormatters";

export default function modelReducer(
  state = initialModelState,
  action: AnyAction
): DependencyState {
  switch (action.type) {
    case types.RECEIVED_DEPENDENCIES_SUCCESS: {
      const distinctColumns = getDistinctColumns(action.data.rowData);
      return {
        ...state,
        dependencies: action.data,
        columnDefinitions: mergedColumns(
          distinctColumns,
          state.columnDefinitions
        ),
        rowData: getRowData(action.data.rowData),
        rowCount: action.data.rowCount ?? state.rowCount,
      };
    }
    case types.RECEIVED_MESSAGE: {
      const message: DependencyData = action.data;
      let newColumns = Array<ColDef>();

      if (message.metaData != null) {
        const metaDataProperties = Object.keys(message.metaData).flat();

        newColumns = metaDataProperties
          .filter(
            (md) =>
              md !== "DependencyName" &&
              !state.columnDefinitions.find(
                (cd: ColDef) => cd.headerName?.toLowerCase() === md.toLowerCase()
              )
          )
          .map((columnName) => {
            return { headerName: columnName, field: columnName };
          });
      }

      const newRowData = getFlattenedRow(message);
      const updatedRowData = state.rowData?.map((rd: DependencyData) => {
        if (rd.partitionKey === newRowData.partitionKey) {
          rd.status = newRowData.status;
          rd.completed = newRowData.completed;
        }
        return rd;
      }) || [newRowData];

      return {
        ...state,
        columnDefinitions: [...state.columnDefinitions, ...newColumns],
        rowData: updatedRowData,
      };
    }
    default:
      return state;
  }
}

export const mergedColumns = (
  newColumns: string[],
  existingColumns: ColDef[]
): ColDef[] => {
  const newCols = newColumns.filter(
    (c) =>
      !existingColumns
        .map((col) => col.field?.toLowerCase())
        .includes(c.toLowerCase())
  );
  if (newCols.length === 0) return existingColumns;
  return [...existingColumns, ...toColumnDefinitions(newCols)];
};

export const getDistinctColumns = (
  dependencyData: Array<DependencyData>
): string[] => {
  const distinctMetaDataValues = [
    ...new Set(
      dependencyData
        .map((item) => {
          let headers: string[] = [];
          if (item.metaData != null) {
            headers = Object.keys(item.metaData).filter(k => k !== "DependencyName").flat();
          }
          return headers;
        })
        .flat()
    ),
  ];
  const columnHeaders = [
    ...new Set(
      dependencyData
        .map((d) =>
          Object.keys(d).filter(
            (x) => x !== "metaData"
          )
        )
        .flat()
    ),
    ...distinctMetaDataValues,
  ];
  return columnHeaders;
};

export function toColumnDefinitions(columnHeaders: string[]): ColDef[] {
  const dateTimeColumns = ["started", "completed"];
  const dateColumns = ["Date", "ValuationDate"];
  const numberColumns = ["AssumptionBasisTypeId", "LiabilityValuationHeaderId", "AllocationHeaderId", "DailyMetricId", "MetricId", "StrategyId", "SchemeId", "ClientAgentId"];
  const groupingColumns = [
    "operationName",
    "partitionKey",
    "status",
    "started",
    "completed",
    "Client",
    "ClientAgent",
    "Scheme",
    "Location",
    "ValuationDate",
    "Date",
    "LiabilityValuationHeaderId",
    "AssumptionBasisType",
    "AssumptionBasisTypeId",
    "AllocationHeaderId",
    "DailyMetricId",
    "MetricId",
    "PythonName",
    "StrategyName",
    "AppName",
    "Type"
  ];

  return [
    ...columnHeaders.map((headerName) => {
      const colDef: ColDef = {
        headerName: capitaliseFirstLetter(headerName),
        field: headerName,
      };
      if (headerName === "dependencyName") {
        colDef.valueGetter = (params) => params.data?.DependencyName;
        colDef.filter = 'dependencyNameFilter';
        colDef.enableRowGroup = true;
        return colDef;
      } else if (headerName === "suspectedStuckAt") {
        // Set by the Housekeeper_StuckOrchestrations zombie-detection ladder (rung 2) when a
        // notified-but-unresponsive operation has no automatic recovery path. Reuses the
        // dateTimeColumn type's filter/comparator but overrides the formatter (null-safe — most
        // rows never have this set) and flags a non-null value visually so it reads as an alert.
        colDef.headerName = "Suspected Stuck";
        colDef.type = "dateTimeColumn";
        colDef.valueFormatter = formatSuspectedStuckAt;
        colDef.cellStyle = (params) =>
          params.value != null
            ? { backgroundColor: "#fdecea", color: "#b71c1c", fontWeight: 600 }
            : null;
        return colDef;
      } else if (dateColumns.includes(headerName)) {
        colDef.type = "dateColumn";
      } else if (dateTimeColumns.includes(headerName)) {
        colDef.type = "dateTimeColumn";
      } else if (headerName === "status") {
        colDef.type = "statusColumn";
      } else if (numberColumns.includes(headerName)) {
        colDef.type = "numberColumn";
      } else {
        colDef.type = "textColumn";
      }

      if (groupingColumns.includes(headerName)) {
        colDef.type = [colDef.type, "groupColumn"];
      }

      return colDef;
    }),
  ];
}

export function capitaliseFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}
